/**
 * Supabase-backed admin auth state.
 *
 * The admin SPA authenticates against the same Supabase project as the main
 * Producer Dashboard app, then validates access through the main Worker's
 * `/api/admin/*` namespace. The server remains the source of truth for
 * `users.is_admin`; client-side role data is only used to gate affordances.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-20-v1
 */
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { create } from 'zustand';
import type { AdminRole } from './types';

export interface AdminSession {
    admin_id: string;
    name: string;
    email: string;
    role: AdminRole;
    initials: string;
    access_token: string;
}

type AdminAuthStatus = 'loading' | 'signed_out' | 'authenticated' | 'forbidden' | 'error';

interface AdminConfigResponse {
    supabase?: {
        url?: unknown;
        anonKey?: unknown;
    };
}

interface AdminWhoAmIResponse {
    id?: unknown;
    name?: unknown;
    email?: unknown;
    role?: unknown;
}

interface AdminAuthStore {
    status: AdminAuthStatus;
    session: AdminSession | null;
    emailHint: string | null;
    error: string | null;
    init: () => Promise<void>;
    refresh: () => Promise<void>;
    signInWithPassword: (email: string, password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    handleApiAuthError: (status: 401 | 403 | 404, detail?: string) => void;
}

class AdminForbiddenError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AdminForbiddenError';
    }
}

class AdminSessionExpiredError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AdminSessionExpiredError';
    }
}

const VALID_ROLES = new Set<AdminRole>(['owner', 'support', 'read_only']);

let supabaseClientPromise: Promise<SupabaseClient> | null = null;
let authListenerBound = false;
let authSyncId = 0;

function asNonEmptyString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function resolveAdminRole(value: unknown): AdminRole {
    return VALID_ROLES.has(value as AdminRole) ? (value as AdminRole) : 'owner';
}

function buildDisplayName(name: string | null, email: string): string {
    if (name) return name;
    const localPart = email.split('@')[0] ?? 'admin';
    return localPart
        .split(/[._-]+/)
        .filter(Boolean)
        .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
        .join(' ');
}

function buildInitials(name: string): string {
    const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
    return initials || 'AD';
}

async function fetchSupabaseConfig(): Promise<{ url: string; anonKey: string }> {
    const res = await fetch('/api/config', {
        method: 'GET',
        headers: {
            accept: 'application/json'
        }
    });

    if (!res.ok) {
        throw new Error(`Unable to load auth config (${res.status})`);
    }

    const body = (await res.json()) as AdminConfigResponse;
    const url = asNonEmptyString(body.supabase?.url);
    const anonKey = asNonEmptyString(body.supabase?.anonKey);

    if (!url || !anonKey) {
        throw new Error('Supabase config is missing url/anonKey');
    }

    return { url, anonKey };
}

function bindAuthListener(client: SupabaseClient) {
    if (authListenerBound) return;

    client.auth.onAuthStateChange((event, session) => {
        if (event === 'TOKEN_REFRESHED' && session?.access_token) {
            useAdminAuthStore.setState((state) => ({
                ...state,
                session: state.session
                    ? {
                          ...state.session,
                          access_token: session.access_token
                      }
                    : state.session
            }));
            return;
        }

        if (event === 'SIGNED_OUT') {
            authSyncId += 1;
            useAdminAuthStore.setState({
                status: 'signed_out',
                session: null,
                emailHint: null,
                error: null
            });
            return;
        }

        void applySession(session, { showLoading: event !== 'INITIAL_SESSION' });
    });

    authListenerBound = true;
}

async function getSupabaseClient(): Promise<SupabaseClient> {
    if (!supabaseClientPromise) {
        supabaseClientPromise = (async () => {
            const config = await fetchSupabaseConfig();
            const client = createClient(config.url, config.anonKey, {
                auth: {
                    autoRefreshToken: true,
                    persistSession: true,
                    detectSessionInUrl: true,
                    flowType: 'implicit'
                }
            });
            bindAuthListener(client);
            return client;
        })();
    }

    return supabaseClientPromise;
}

async function verifyAdminAccess(session: Session): Promise<AdminSession> {
    const res = await fetch('/api/admin/overview/_whoami', {
        method: 'GET',
        headers: {
            accept: 'application/json',
            authorization: `Bearer ${session.access_token}`
        }
    });

    const text = await res.text();
    let body: unknown = null;
    try {
        body = text ? (JSON.parse(text) as AdminWhoAmIResponse) : null;
    } catch {
        body = text;
    }

    if (res.status === 401) {
        throw new AdminSessionExpiredError('Session expired. Sign in again.');
    }

    if (res.status === 403 || res.status === 404) {
        throw new AdminForbiddenError(
            'This account is not marked admin in the existing users table.'
        );
    }

    if (!res.ok) {
        throw new Error(`Admin access check failed (${res.status})`);
    }

    const email =
        asNonEmptyString((body as AdminWhoAmIResponse | null)?.email) ??
        asNonEmptyString(session.user.email) ??
        'unknown@producerdashboard.app';
    const name = buildDisplayName(
        asNonEmptyString((body as AdminWhoAmIResponse | null)?.name) ??
            asNonEmptyString(session.user.user_metadata?.full_name) ??
            asNonEmptyString(session.user.user_metadata?.name),
        email
    );
    const role = resolveAdminRole(
        (body as AdminWhoAmIResponse | null)?.role ??
            session.user.app_metadata?.admin_role ??
            session.user.user_metadata?.admin_role
    );

    return {
        admin_id: asNonEmptyString((body as AdminWhoAmIResponse | null)?.id) ?? session.user.id,
        name,
        email,
        role,
        initials: buildInitials(name),
        access_token: session.access_token
    };
}

async function applySession(session: Session | null, opts: { showLoading?: boolean } = {}) {
    const syncId = ++authSyncId;

    if (opts.showLoading ?? true) {
        useAdminAuthStore.setState((state) => ({
            ...state,
            status: 'loading',
            error: null
        }));
    }

    if (!session) {
        useAdminAuthStore.setState({
            status: 'signed_out',
            session: null,
            emailHint: null,
            error: null
        });
        return;
    }

    try {
        const adminSession = await verifyAdminAccess(session);
        if (syncId !== authSyncId) return;

        useAdminAuthStore.setState({
            status: 'authenticated',
            session: adminSession,
            emailHint: adminSession.email,
            error: null
        });
    } catch (error) {
        if (syncId !== authSyncId) return;

        if (error instanceof AdminForbiddenError) {
            useAdminAuthStore.setState({
                status: 'forbidden',
                session: null,
                emailHint: session.user.email ?? null,
                error: error.message
            });
            return;
        }

        if (error instanceof AdminSessionExpiredError) {
            useAdminAuthStore.setState({
                status: 'signed_out',
                session: null,
                emailHint: session.user.email ?? null,
                error: error.message
            });
            return;
        }

        useAdminAuthStore.setState({
            status: 'error',
            session: null,
            emailHint: session.user.email ?? null,
            error: error instanceof Error ? error.message : 'Unexpected auth error'
        });
    }
}

async function resolveActiveSession() {
    const client = await getSupabaseClient();
    const {
        data: { session },
        error
    } = await client.auth.getSession();

    if (error) {
        throw error;
    }

    await applySession(session, { showLoading: true });
}

export const useAdminAuthStore = create<AdminAuthStore>((set) => ({
    status: 'loading',
    session: null,
    emailHint: null,
    error: null,
    init: async () => {
        if (!supabaseClientPromise) {
            set({
                status: 'loading',
                session: null,
                emailHint: null,
                error: null
            });
        }
        try {
            await resolveActiveSession();
        } catch (error) {
            set({
                status: 'error',
                session: null,
                emailHint: null,
                error: error instanceof Error ? error.message : 'Unable to initialize auth'
            });
        }
    },
    refresh: async () => {
        set((state) => ({
            ...state,
            status: 'loading',
            error: null
        }));
        try {
            await resolveActiveSession();
        } catch (error) {
            set({
                status: 'error',
                session: null,
                emailHint: null,
                error: error instanceof Error ? error.message : 'Unable to refresh auth'
            });
        }
    },
    signInWithPassword: async (email, password) => {
        const trimmedEmail = email.trim().toLowerCase();
        if (!trimmedEmail || !password) {
            set({
                status: 'signed_out',
                session: null,
                emailHint: trimmedEmail || null,
                error: 'Email and password are required.'
            });
            return;
        }

        set((state) => ({
            ...state,
            status: 'loading',
            error: null,
            emailHint: trimmedEmail
        }));

        try {
            const client = await getSupabaseClient();
            const { data, error } = await client.auth.signInWithPassword({
                email: trimmedEmail,
                password
            });

            if (error) throw error;

            await applySession(data.session, { showLoading: false });
        } catch (error) {
            set({
                status: 'signed_out',
                session: null,
                emailHint: trimmedEmail,
                error: error instanceof Error ? error.message : 'Sign-in failed'
            });
        }
    },
    signInWithGoogle: async () => {
        set((state) => ({
            ...state,
            status: 'loading',
            error: null
        }));

        try {
            const client = await getSupabaseClient();
            const { error } = await client.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/`,
                    scopes: 'email profile',
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'select_account'
                    }
                }
            });

            if (error) throw error;
        } catch (error) {
            set((state) => ({
                ...state,
                status: 'signed_out',
                error: error instanceof Error ? error.message : 'Google sign-in failed'
            }));
        }
    },
    signOut: async () => {
        try {
            const client = await getSupabaseClient();
            await client.auth.signOut();
        } finally {
            authSyncId += 1;
            set({
                status: 'signed_out',
                session: null,
                emailHint: null,
                error: null
            });
        }
    },
    handleApiAuthError: (status, detail) => {
        const current = useAdminAuthStore.getState();
        const emailHint = current.session?.email ?? current.emailHint ?? null;

        if (status === 401) {
            authSyncId += 1;
            set({
                status: 'signed_out',
                session: null,
                emailHint,
                error: detail || 'Session expired. Sign in again.'
            });
            return;
        }

        set({
            status: 'forbidden',
            session: null,
            emailHint,
            error: detail || 'This account is not marked admin in the existing users table.'
        });
    }
}));

export function useAdminSession(): AdminSession {
    const session = useAdminAuthStore((state) => state.session);
    if (!session) {
        throw new Error('Admin session unavailable outside authenticated state');
    }
    return session;
}

export function useAdminAuth() {
    return useAdminAuthStore();
}

export async function getAccessToken(): Promise<string | null> {
    const currentToken = useAdminAuthStore.getState().session?.access_token;
    if (currentToken) return currentToken;

    const client = await getSupabaseClient();
    const {
        data: { session }
    } = await client.auth.getSession();
    return session?.access_token ?? null;
}

export function handleAdminApiAuthError(status: 401 | 403 | 404, detail?: string) {
    useAdminAuthStore.getState().handleApiAuthError(status, detail);
}

export function canPerform(role: AdminRole, action: 'owner_only' | 'support_ok' | 'read'): boolean {
    if (action === 'read') return true;
    if (action === 'support_ok') return role !== 'read_only';
    return role === 'owner';
}
